<template>
  <div class="drink-plan-option">
    <div class="row align-items-center">
      <div class="col-12 col-sm-7">
        <label :for="`drink-plan-${drinkPlan.id}`" class="mb-0">{{drinkPlan.name}}</label>
        <!-- オプションの条件・補足(例: ビール（発泡酒）は乾杯のみ)。チェック欄とは別に、プラン自体の説明として出す -->
        <div v-for="note in notes" :key="note" class="drink-plan-note text-muted">※ {{note}}</div>
      </div>
      <div class="col-5 col-sm-2">{{drinkPlan.price | currency}}</div>
      <div class="col-7 col-sm-3">
        <!-- 自動で入れた人数は、気づかない人がいないようオレンジで目立たせる(手で変えたら普通の見た目に戻る) -->
        <div class="input-group" :class="{ 'drink-plan-autofilled': autoFilled }">
          <input :id="`drink-plan-${drinkPlan.id}`" type="number" min="0" class="form-control" v-model.number="quantity" @input="onManualInput" inputmode="numeric">
          <div class="input-group-append">
            <span class="input-group-text">人</span>
          </div>
        </div>
        <div v-if="autoFilled" class="drink-plan-autofilled-note">お料理と同じ人数を自動で入れています</div>
      </div>
    </div>
    <!-- オプション(旧「ドリンクプラン変更」)。1行に収めて、プランが6つ並んでも縦に長くならないようにする -->
    <div v-if="hasCustomizations" class="customization-area">
      <div v-for="customization in drinkPlan.customizations" :key="customization.id" class="form-check customization-row">
        <label class="form-check-label">
          <input type="checkbox"
                class="form-check-input"
                :checked="isSelected(customization.id)"
                @change="toggleCustomization(customization.id, $event.target.checked)">
          <span class="customization-tag">オプション</span>
          <span>{{customization.description}}</span>
          <template v-if="Number(customization.price) > 0">
            <span class="ml-1 text-danger">＋{{customization.price | currency}}/人</span>
            <span class="ml-1 text-muted customization-total">（{{priceWith(customization) | currency}}/人になります）</span>
          </template>
          <span class="ml-1 text-muted" v-else>無料</span>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    props: {
      drinkPlan: Object,
      handleDrinkPlan: Function,
      calculate: Function,
      validatesQuantity: Function,
      // おすすめプラン案内ページから引き継いだ人数。あればその人数を最初から入れて親に知らせる
      initialQuantity: { type: Number, default: null },
      // 親の「飲み放題をつける／つけない」と人数に合わせた人数。null のときは何もしない(手入力のまま)
      syncedQuantity: { type: Number, default: null },
    },
    data() {
      return {
        quantity: '',
        selectedCustomizationIds: [],
        // 親(飲み放題「つける」＋人数)から自動で入れた人数か。手で変えたら false
        autoFilled: false
      }
    },
    computed: {
      hasCustomizations() {
        return Array.isArray(this.drinkPlan.customizations) && this.drinkPlan.customizations.length > 0
      },
      // 条件・補足(note)が入っているオプションだけ、重複を除いて集める
      notes() {
        if (!this.hasCustomizations) return []
        const notes = this.drinkPlan.customizations.map(c => (c.note || '').trim()).filter(n => n !== '')
        return notes.filter((n, i) => notes.indexOf(n) === i)
      }
    },
    created() {
      if (this.initialQuantity > 0) {
        this.quantity = this.initialQuantity
        this.emitChange()
      }
    },
    watch: {
      // 親で人数や「つける／つけない」が変わったら、この欄の人数もそろえる(0 なら空欄にして外す)
      syncedQuantity: {
        immediate: true,
        handler(value) {
          if (value === null || value === undefined) return
          const next = value > 0 ? value : ''
          this.autoFilled = value > 0
          if (this.quantity === next) return
          this.quantity = next
          this.emitChange()
        }
      }
    },
    methods: {
      onManualInput() {
        this.autoFilled = false
        this.emitChange()
      },
      // オプションを付けたときの1人あたりの金額(プラン単価 + オプション単価)
      priceWith(customization) {
        return Number(this.drinkPlan.price) + Number(customization.price)
      },
      isSelected(id) {
        return this.selectedCustomizationIds.includes(id)
      },
      toggleCustomization(id, checked) {
        if (checked) {
          if (!this.selectedCustomizationIds.includes(id)) {
            this.selectedCustomizationIds = [...this.selectedCustomizationIds, id]
          }
        } else {
          this.selectedCustomizationIds = this.selectedCustomizationIds.filter(x => x !== id)
        }
        this.emitChange()
      },
      buildSelectedCustomizations() {
        if (!this.hasCustomizations) return []
        return this.drinkPlan.customizations
          .filter(c => this.selectedCustomizationIds.includes(c.id))
          .map(c => ({
            drink_plan_customization_id: c.id,
            description: c.description,
            price: c.price
          }))
      },
      emitChange() {
        const { name, price } = this.drinkPlan
        this.$emit('handleDrinkPlan', {
          name,
          price,
          quantity: this.quantity,
          customizations: this.buildSelectedCustomizations()
        })
        this.$emit('validatesQuantity')
        this.$emit('calculate')
      }
    }
  }
</script>

<style scoped>
  .drink-plan-option {
    margin-bottom: 8px;
  }

  .drink-plan-note {
    font-size: 0.85em;
    margin-top: 2px;
  }

  .drink-plan-autofilled .form-control {
    background: #fff7e6;
    border-color: #e8833a;
    font-weight: bold;
    color: #a63508;
  }

  .drink-plan-autofilled .input-group-text {
    background: #e8833a;
    border-color: #e8833a;
    color: #fff;
  }

  .drink-plan-autofilled-note {
    font-size: 0.8em;
    color: #a63508;
    margin-top: 2px;
    text-align: right;
  }

  .customization-area {
    margin: 2px 0 0 12px;
    padding: 2px 8px;
    border-left: 3px solid #cfd8d3;
    background: #f6f8f7;
    font-size: 0.9em;
  }

  .customization-row {
    margin-bottom: 0;
    line-height: 1.6;
  }

  .customization-tag {
    display: inline-block;
    font-size: 0.8em;
    color: #6c757d;
    border: 1px solid #cfd8d3;
    border-radius: 3px;
    padding: 0 4px;
    margin-right: 4px;
    line-height: 1.4;
  }

  .customization-total {
    font-size: 0.9em;
  }
</style>
